import React, {useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';

import {ModalContend, ModalDetalleArticulo, NameProduct} from '../../ui';
import {ProductPriceUI} from './ProductPriceUI';
import {AddToFavoritesButton} from './AddToFavoritesButton';
import {NumberInputUI} from '../../ui/components/NumberInputUI';
import {AvailabilityUI} from '../../common/ui/AvailabilityUI';
import {TagListUI} from '../../common/ui/TagListUI';
import {AddUpdateButton} from '../../common/ui/AddUpdateButton';
import {BtnCloseCarrito} from '../../ui/components/navbar/NavbarStyles.elements';
import {PurchasePanel} from './PurchasePanel';
import {TrackProductButton} from '../../common/ui/TrackProductButton';

import {useProductSelection} from '../application/useProductSelection';
import {useCartContext} from '../../cart';
import {useTrackingProduct} from '../application/useTrackingProduct';

import {buildWithoutDeal} from '../domain/ProductSelection.model';
import {generateItemTags} from '../../common/CatalogService';

/**
 * @param {boolean} value
 * @param {Function} setHover
 * @returns {Function}
 */
const createHoverHandler = (value, setHover) => () => setHover(value);

/**
 * @param {Function} navigate
 * @param {string} productCode
 * @returns {Function}
 */
const createNavigateHandler = (navigate, productCode) =>
    () => navigate(`/articulos/${encodeURIComponent(productCode)}`);

/**
 * @param {Object} details
 * @param {Function} addProduct
 * @param {number} quantity
 * @returns {Promise<void>}
 */
const handleDirectAddToCart = async (details, addProduct, quantity) => {
    try {
        await addProduct(buildWithoutDeal(details, quantity));
        toast.success(`'${details.name || details.code}' agregado al carrito exitosamente.`);
    } catch (error) {
        toast.error(`'${details.name || details.code}' no pudo agregarse al carro. Por favor, reintente más tarde o contacte con nosotros.`);
    }
};

/**
 * Renders the modal content for selecting product deal and quantity.
 * @param {Object} product - Product details.
 * @param {number} quantity - Default selected quantity.
 * @param {Function} onClose - Callback to close the modal.
 * @returns {JSX.Element}
 */
const renderModalContent = (product, quantity, onClose) => (
    <ModalContend>
        <ModalDetalleArticulo>
            <div className="d-flex flex-row gap-2 align-content-center">
                <h3>{product.name}</h3>
                <BtnCloseCarrito onClick={onClose}>
                    <CloseIcon/>
                </BtnCloseCarrito>
            </div>
            <PurchasePanel
                product={product}
                defaultQuantity={quantity}
                afterAddToCart={onClose}
            />
        </ModalDetalleArticulo>
    </ModalContend>
);

/**
 * @param {Object} props
 * @param {Object} props.details
 * @returns {JSX.Element}
 */
export const ProductFlatPurchaseCardUI = ({details}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [modal, setModal] = useState(false);

    const navigate = useNavigate();
    const {productSelection, setQuantity} = useProductSelection(details, 1);
    const {isOnCart, addProduct} = useCartContext();
    const {isTracked, handleTrack, handleUntrack} = useTrackingProduct(details);

    const tags = useMemo(() => generateItemTags(details), [details]);

    const handleMouseEnter = useCallback(createHoverHandler(true, setIsHovered), []);
    const handleMouseLeave = useCallback(createHoverHandler(false, setIsHovered), []);
    const handleNavigate = useCallback(createNavigateHandler(navigate, details.code), [navigate, details.code]);

    const hasStock = useMemo(() =>
            (details.availableUnits > 0 || details.onDemand),
        [details.availableUnits, details.onDemand]
    );

    const disabled = useMemo(() =>
            (!details.price || details.price?.bestPrice <= 0 || productSelection.quantity <= 0 || !hasStock),
        [details, productSelection.quantity]
    );

    const handleAddToCart = useCallback(() => {
        if (details.hasDealsToChoose) {
            setModal(true);
        } else {
            handleDirectAddToCart(details, addProduct, productSelection.quantity);
        }
    }, [details, addProduct, productSelection.quantity]);

    const handleTrackSuccess = () => toast.success("Producto agregado a tus productos pendientes!");
    const handleTrackError = () => toast.error("Error al agregar producto a tus productos pendientes!");
    const handleUntrackSuccess = () => toast.success("Producto borrado de tus productos pendientes!");
    const handleUntrackError = () => toast.error("Error al borrar producto de tus productos pendientes!");

    return (
        <div
            className="rounded-2 p-1 shadow-sm"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="row justify-content-start align-items-center">
                <div className="col-12 col-md-2 col-xl-auto">
                    <div className="d-flex justify-content-center align-items-center">
                        <div className="position-absolute">
                            <AddToFavoritesButton
                                data={{
                                    productCode: details.code,
                                    show: isHovered,
                                    isFavorite: details.isFavorite,
                                }}
                            />
                        </div>
                        <img
                            src={details.imageUrl}
                            onClick={handleNavigate}
                            style={{height: '65px', width: '65px', objectFit: 'contain'}}
                            className="rounded"
                            alt="Imagen del producto"
                        />
                    </div>
                </div>

                <div className="col-12 col-md-8 col-xl">
                    <div className="row gap-2 gap-lg-0">
                        <div className="col-12 col-lg-8 col-xl-8">
                            <div
                                className="d-flex flex-row justify-content-between"
                                style={{cursor: 'pointer'}}
                                onClick={handleNavigate}
                            >
                                <div className="d-flex flex-column">
                                    <TagListUI data={{tags}}/>
                                    <NameProduct>
                                        {details.name}
                                        <small className="text-muted ms-1">Ref: {details.code}</small>
                                    </NameProduct>
                                    <AvailabilityUI data={{availability: productSelection.availability}}/>
                                </div>
                                <ProductPriceUI
                                    price={{
                                        sellingPrice: details.price?.bestPrice,
                                        basePrice: details.price?.value,
                                    }}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-lg-4 col-xl-4">
                            <div className="d-flex flex-row gap-2 justify-content-end pe-2">
                                {(isTracked || !hasStock) && (
                                    <TrackProductButton
                                        isTracked={isTracked}
                                        onTrack={() => handleTrack(handleTrackSuccess, handleTrackError)}
                                        onUntrack={() => handleUntrack(handleUntrackSuccess, handleUntrackError)}
                                    />
                                )}
                                <NumberInputUI
                                    data={{value: productSelection.quantity}}
                                    actions={{setValue: setQuantity}}
                                />
                                <div>
                                    {modal && renderModalContent(details, productSelection.quantity, () => setModal(false))}
                                    <div style={{minWidth: '135px'}}>
                                        <AddUpdateButton
                                            shouldAdd={isOnCart(details?.id)}
                                            disabled={disabled}
                                            onClick={handleAddToCart}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
